import { createEvent, sample, combine, createStore, createEffect, attach } from 'effector'

import { trackEvent } from 'helpers/analytics'
import { goTo, urlTo } from 'helpers/router'

import {
  $baseProviders,
  $nomineesTransferProviderList,
  $skeletonProviders,
  fetchNomineesTransferProvidersFx,
  fetchProvidersIfNeeded,
  NomineesTransferProviderRegulatoryType,
} from 'app/effector/nominees-transfers/providers'
import { fetchActiveTransferRequestsFx } from 'app/effector/nominees-transfers/request/sipp'
import { fetchZendeskArticlesIfNeeded } from 'app/effector/zendesk-articles'

type PageUrl = null | string

// events
const setPrevPage = createEvent<PageUrl>()
const initTransferCentre = createEvent()
const prefetchPageData = createEvent()
const goBack = createEvent()

// effects
const navigateToUrlFx = createEffect((pageUrl: PageUrl): void => {
  goTo(pageUrl ?? urlTo('dashboard'))
})
const fetchTransferCentreActiveTransferRequestsFx = attach({
  effect: fetchActiveTransferRequestsFx,
})
const sendTransferCentreOpenedEventFx = createEffect(() => {
  trackEvent({ action: 'transfer_centre_opened' })
})

// stores
const $shouldShowSkeletonProviders = combine(
  $nomineesTransferProviderList,
  fetchNomineesTransferProvidersFx.pending,
  (providers, providersPending) => providers.length < 1 && providersPending,
)

const $displayedProviders = combine(
  $shouldShowSkeletonProviders,
  $baseProviders,
  $skeletonProviders,
  (shouldShowSkeleton, baseProviders, skeletonProviders) => {
    return shouldShowSkeleton ? skeletonProviders : baseProviders
  },
)

const $prevPage = createStore<PageUrl>(null).on(setPrevPage, (_, prevPage) => prevPage)

// logic
sample({
  clock: initTransferCentre,
  target: [prefetchPageData, sendTransferCentreOpenedEventFx],
})

sample({
  clock: prefetchPageData,
  fn: () => 'SIPP-TRANSFERS',
  target: fetchZendeskArticlesIfNeeded,
})

sample({
  clock: prefetchPageData,
  fn: () => NomineesTransferProviderRegulatoryType.SIPP,
  target: [fetchProvidersIfNeeded, fetchTransferCentreActiveTransferRequestsFx],
})

sample({
  clock: goBack,
  source: $prevPage,
  target: navigateToUrlFx,
})

export { initTransferCentre, $shouldShowSkeletonProviders, $displayedProviders, $prevPage, goBack, setPrevPage }
